import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { formatUtcMonth } from '@/lib/format'
import { usePreviewedChange } from '../hooks/usePreviewedChange'
import { useUpdateBillingAddonMutation } from '../hooks/useUpdateBillingAddonMutation'
import { formatAddonPercent } from '../lib/addons'
import { rateInput } from '../lib/numberInput'
import { utcMonthOf } from '../lib/periods'
import {
  ADDON_LABELS,
  type AddonChange,
  type AddonChangeRequest,
  type AddonSetting,
  type BillingAddon,
} from '../types'

/** What the Add-ons tab says about the form, as the sheet's description
 *  while that tab is open. */
export const ADDON_FORM_DESCRIPTION =
  "An add-on charges a percentage of each month's usage total, from this month until it is turned off. Earlier months keep what they were billed."

// `billing_addons.fraction` is numeric(5,4): a percentage to two places.
const PERCENT_DECIMAL_PLACES = 2

/** Why a typed percentage cannot be previewed, or null when it can. The
 *  field takes only digits and a point, so it is never negative. */
function percentProblem(value: string): string | null {
  const percent = Number(value)
  if (!Number.isFinite(percent)) return 'Enter a number.'
  if (percent > 100) return 'Between 0 and 100. 0 turns it off.'
  const places = value.split('.')[1]?.length ?? 0
  if (places > PERCENT_DECIMAL_PLACES)
    return `Up to ${PERCENT_DECIMAL_PLACES} decimal places.`
  return null
}

/** Lets Careforce staff preview, then write, switching an add-on on, changing
 *  its percentage or turning it off, from the current month (UTC). */
export function ChangeAddonForm({
  addons,
  writing,
  setWriting,
  onDone,
}: {
  addons: AddonSetting[]
  /** A write is in flight; held by the sheet, which stays open meanwhile. */
  writing: boolean
  setWriting: (writing: boolean) => void
  onDone: () => void
}) {
  const { preview, error, busy, edit, requestPreview, apply } =
    usePreviewedChange(useUpdateBillingAddonMutation(), {
      writing,
      setWriting,
      onDone,
    })
  const [addon, setAddon] = useState<BillingAddon | ''>('')
  const [percent, setPercent] = useState('')
  const [reason, setReason] = useState('')

  const problem = percent === '' ? null : percentProblem(percent)
  const canPreview = addon !== '' && percent !== '' && problem === null

  function previewChange() {
    if (addon === '') return
    const trimmedReason = reason.trim()
    const request: AddonChangeRequest = {
      addon,
      percent: Number(percent),
      ...(trimmedReason ? { note: trimmedReason } : {}),
      confirm: false,
    }
    requestPreview(request)
  }

  return (
    <div className="grid gap-4">
      <div className="grid gap-2">
        <Label htmlFor="addon">Add-on</Label>
        <Select
          value={addon}
          disabled={busy}
          onValueChange={(value) =>
            edit(() => setAddon(value as BillingAddon))
          }>
          <SelectTrigger id="addon" aria-label="Add-on">
            <SelectValue placeholder="Choose an add-on" />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(ADDON_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid gap-2">
        <Label htmlFor="addon-percent">Percent of the bill</Label>
        <Input
          id="addon-percent"
          inputMode="decimal"
          disabled={busy || addon === ''}
          placeholder={
            addon === ''
              ? undefined
              : `Now: ${formatAddonPercent(addons, addon)}`
          }
          value={percent}
          aria-invalid={problem !== null}
          aria-describedby={
            problem
              ? 'addon-percent-hint addon-percent-problem'
              : 'addon-percent-hint'
          }
          onChange={(e) => {
            const value = rateInput(e.target.value)
            if (value === null) return
            edit(() => setPercent(value))
          }}
        />
        <p id="addon-percent-hint" className="text-xs text-muted-foreground">
          10 for 10%. 0 turns it off.
        </p>
        {problem && (
          <p
            id="addon-percent-problem"
            className="text-xs text-error-low-contrast">
            {problem}
          </p>
        )}
      </div>
      <div className="grid gap-2">
        <Label htmlFor="addon-reason">Reason</Label>
        <Input
          id="addon-reason"
          disabled={busy}
          value={reason}
          onChange={(e) => edit(() => setReason(e.target.value))}
        />
      </div>

      {preview && <AddonChangePreview change={preview.change} />}

      {error && (
        <p role="alert" className="text-sm text-error-low-contrast">
          {error}
        </p>
      )}

      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          disabled={!canPreview || busy}
          onClick={previewChange}>
          Preview
        </Button>
        <Button
          disabled={!preview || busy}
          onClick={() =>
            preview &&
            apply({
              ...preview.request,
              from_month: preview.change.from_month,
              previewed_addon_version: preview.change.addon_in_force_version,
            })
          }>
          Apply change
        </Button>
      </div>
    </div>
  )
}

/** What a previewed change does: the month it starts in, and each
 *  consequence for the bill, as the server states it. */
function AddonChangePreview({ change }: { change: AddonChange }) {
  const startsIn = formatUtcMonth(utcMonthOf(`${change.from_month}-01`))
  return (
    <div
      role="note"
      className="grid gap-2 rounded-md border border-dashed border-border px-3 py-2 text-xs">
      <p className="font-medium">
        {/* A null percent is an add-on that is off, which the page shows as 0%. */}
        {`${ADDON_LABELS[change.addon]}: ${change.current_percent ?? 0}% → ${
          change.proposed_percent ?? 0
        }%, from ${startsIn}`}
      </p>
      <ul className="grid list-disc gap-1 pl-4 text-muted-foreground">
        {change.consequences.map((sentence) => (
          <li key={sentence}>{sentence}</li>
        ))}
      </ul>
    </div>
  )
}
